'use client'

// Region-change + shipping-country overlay wiring (Overmind retirement, Phase D2).
//
// This module reimplements the LAST piece of real Overmind logic — the in-place
// payment-page region change (the former `selectedCountryChanged` action) and the
// post-mount shipping-country overlay/refinement (the former
// `applyShippingCountryOverlay` pipe) — as plain functions over the Zustand stores,
// with NO Overmind. It preserves the EXACT refetch set, prune, and ordering the
// Overmind pipes had, minus the writes that no longer have a live reader (see the
// FLAG on the surface refetches below).
//
// WHY A MODULE OF getState() FUNCTIONS (not just a hook): `applyShippingCountryOverlay`
// runs from app/providers.jsx's GLOBAL post-mount bootstrap effect (not a component
// render), so it must be callable outside React. `changeRegion` runs from a click
// handler. Both drive the stores via getState(), so they live as plain async
// functions; `useRegionChange()` is a thin hook that hands `changeRegion` to the
// AddressInput country picker.

import { http } from '../utils/clientHttp'
import { useRegionStore } from '../store/regionStore'
import { useOffersStore } from '../store/offersStore'
import { useCartStore } from '../store/cartStore'

/**
 * Fetch the region shipping info (ships-from / transit / free-shipping) for a
 * pricing country. Mirrors operators.getShippingInfo's request. Returns the data
 * or null on failure (leaves the caller's current shippingInfo untouched — unlike
 * the old operator's stray `state.countries = []` catch, which is deliberately NOT
 * reproduced: it would wrongly clear the country picker list).
 * @param {string|null} region
 * @returns {Promise<any|null>}
 */
async function fetchShippingInfo(region) {
  try {
    const resp = await http.get(`/api/getShippingInfo?region=${region}`)
    return resp && resp.data ? resp.data : null
  } catch (e) {
    return null
  }
}

/**
 * The in-place payment-page region change. Byte-for-byte the former Overmind
 * `selectedCountryChanged` pipe, minus the dead surface refetches:
 *   1. isRegionChanging = true                       (was state.isLoading = true)
 *   2. regionStore.setNewRegion(newRegion)           (was setRegionInStorage +
 *      syncRegionStoreOnCountryChange — updates newRegion + sessionStorage)
 *   3. parallel: refined shippingInfo (regionStore) + FULL offers (offersStore)
 *      (was getShippingInfo + refetchOffersStoreForRegion)
 *   4. prune the Zustand cart against the just-refetched offers
 *      (was removeUnavailableShoppingCartItems (dead Overmind cart) +
 *       pruneZustandCartForRegion)
 *   5. isRegionChanging = false
 *
 * FLAG — the old pipe ALSO refetched namedPrices / subscriptions / add-ons /
 * turnkeyOptions into Overmind state. After D2 those surfaces are served from the
 * server-props providers (immutable React context set once at RegionPageClient), so
 * an in-place region change can no longer update them regardless — AND on the
 * server-props path (production) the old refetch was already ignored (the providers
 * prefer their non-empty server context over the Overmind store). The checkout page
 * renders none of those surfaces (only offers-priced cart items, repriced above via
 * the offers store), so dropping the refetches is parity-preserving there. See the
 * report's parity notes.
 *
 * @param {string} newRegion  the picked country code (e.g. 'FR')
 * @returns {Promise<void>}
 */
export async function changeRegion(newRegion) {
  const region = useRegionStore.getState()
  region.setRegionChanging(true)
  try {
    region.setNewRegion(newRegion)
    await Promise.all([
      fetchShippingInfo(newRegion).then((si) => {
        if (si) useRegionStore.getState().setShippingInfo(si)
      }),
      useOffersStore.getState().ensureFullOffers(newRegion),
    ])
    // Prune the live Zustand cart against the just-refetched region offers (keeps
    // existing-but-region-unavailable items greyed out; drops only ids gone from
    // the catalog) — exactly as pruneZustandCartForRegion did.
    useCartStore.getState().pruneMissing(useOffersStore.getState().offersFull)
  } finally {
    useRegionStore.getState().setRegionChanging(false)
  }
}

/**
 * Sub-region VAT/availability + shipping refinement. Mirrors the former
 * operators.refetchShopItemsForShippingCountry (#188): when the visitor's precise
 * shipping country differs from the region representative the SSR priced against
 * (GB/FR/IT/ES on /eu), refine BOTH the offers (offers store) and the shipping info
 * (region store) for that precise country. Gated identically so a
 * region-representative visitor short-circuits (the #188 anti-refetch dedup).
 *
 * DEDUP KEY: the country the page was priced against. On the SERVER-PROPS path the
 * offers store never client-fetched, so offersStore.country stays null; the SSR
 * offers/shipping were priced against the region representative (regionStore.
 * newRegion, e.g. 'DE' on /eu, resolved on the client by initClientRegion). Fall
 * back to it — gating on `!offersCountry` alone skipped this for EVERY sub-region
 * country, so GB never got its Corby ships-from + UK stock (it showed the EU ones).
 * `!representative` still short-circuits a bare content page (no offers, no region).
 * @returns {Promise<void>}
 */
export async function refineForShippingCountry() {
  const cc = useRegionStore.getState().shippingCountry
  const representative = useOffersStore.getState().country || useRegionStore.getState().newRegion
  if (!cc || !representative || cc === representative) {
    return
  }
  try {
    const [, si] = await Promise.all([
      // offers → Zustand offers store (degrades images internally, updates country)
      useOffersStore.getState().refetchForShippingCountry(cc),
      // shippingInfo → Zustand region store (the RegionGeo seam prefers it)
      fetchShippingInfo(cc),
    ])
    if (si) useRegionStore.getState().setShippingInfo(si)
  } catch (e) {
    // keep the region-level offers/shipping on failure
  }
}

/**
 * The post-mount shipping-country overlay. Reimplements the former
 * `applyShippingCountryOverlay` Overmind pipe: resolve the picker's displayed
 * country (cookie → geo-IP, via the region store) THEN run the sub-region offers +
 * shipping refinement, in that order (so the refinement sees the resolved country).
 * Called from app/providers.jsx's global post-mount bootstrap. Idempotent (the
 * store overlay is guarded by _hasOverlaid; the refinement dedups on offers
 * country).
 * @returns {Promise<void>}
 */
export async function applyShippingCountryOverlay() {
  await useRegionStore.getState().overlayShippingCountry()
  await refineForShippingCountry()
}

/**
 * The AddressInput country picker's seam: hands the click handler `changeRegion`.
 * (The loading flag for the picker disable is read directly off the region store by
 * StatesAutocomplete via selectRegionChanging, so it is not returned here.)
 * @returns {{ changeRegion: (newRegion: string) => Promise<void> }}
 */
export function useRegionChange() {
  return { changeRegion }
}

export default useRegionChange
